// Shared page states. Every state says what happened and what to do next.
// Copy comes from HourSign App.dc.html ("Empty, loading, error"). Never the word
// "invalid", never a pattern in a message.
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router";
import {
  CloudOff,
  FileQuestion,
  Inbox,
  Lock,
  RefreshCw,
  TriangleAlert,
  UserRoundX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Body, BodySm, H3, MonoSm, Overline } from "@/components/ui/typography";
import { ApiError } from "@/lib/api";
import { IconTile, type IconTileTone } from "@/components/hoursign/IconTile";
import { cn } from "@/lib/utils";

/**
 * One card, one geometry: 18px radius, a 40px coloured tile holding a 19px glyph, a
 * title on the h3 step and a body no wider than 46 characters.
 */
function Shell({
  icon,
  title,
  body,
  action,
  aside,
  tone = "primary",
  tileVariant = "soft",
  dashed = false,
  className,
}: {
  icon: LucideIcon;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  aside?: ReactNode;
  tone?: IconTileTone;
  tileVariant?: "solid" | "soft";
  /** The empty state is the only dashed card in the product. */
  dashed?: boolean;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "bg-card rounded-card mx-auto flex max-w-[40rem] flex-col items-center gap-3 border px-4 py-12 text-center",
        dashed ? "border-border-strong border-dashed" : "border-border",
        tone === "destructive" && "border-destructive/30",
        className,
      )}
    >
      <IconTile icon={icon} tone={tone} variant={tileVariant} size="md" label={title} labelled />
      <H3>{title}</H3>
      {body ? <Body className="text-muted-foreground max-w-[46ch]">{body}</Body> : null}
      {action}
      {aside}
    </div>
  );
}

/** Skeletons while the first load runs. Same shape as the content that replaces it. */
export function LoadingState({ rows = 4, label = "Loading" }: { rows?: number; label?: string }) {
  return (
    <div aria-busy="true" aria-live="polite" className="flex flex-col gap-3">
      <span className="sr-only">{label}</span>
      <Skeleton className="h-7 w-48" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="rounded-card h-16 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return <Shell dashed icon={Inbox} {...{ title, body, action }} />;
}

export function ErrorState({
  title = "We couldn't load this",
  error,
  onRetry,
}: {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
}) {
  const apiError = error instanceof ApiError ? error : undefined;
  return (
    <Shell
      tone="destructive"
      icon={TriangleAlert}
      title={title}
      body={
        apiError?.message ??
        "Nothing was lost — your entries are safe. This was a connection problem on our side."
      }
      action={
        onRetry ? (
          <Button onClick={onRetry}>
            <RefreshCw aria-hidden="true" /> Try again
          </Button>
        ) : undefined
      }
      aside={
        apiError?.requestId ? (
          <MonoSm className="text-muted-foreground tabular-nums">{apiError.requestId}</MonoSm>
        ) : undefined
      }
    />
  );
}

/** The whole page, when the browser is offline. The banner is the inline version. */
export function OfflineState() {
  return (
    <Shell
      tone="group"
      tileVariant="solid"
      className="bg-warning text-warning-foreground border-warning-border/40"
      icon={CloudOff}

      title="You're offline"
      body={
        <span className="text-warning-foreground">
          Keep logging — entries are saved on this device and sent when you&apos;re back. Signing
          needs a connection.
        </span>
      }
    />
  );
}

/** Locked is a state, not an error: the reason, then the way forward. */
export function LockedState({
  title = "This period is signed",
  reason,
  amendHref,
}: {
  title?: string;
  reason: string;
  amendHref?: string;
}) {
  return (
    <Shell
      tone="info"
      icon={Lock}
      title={title}
      body={reason}
      action={
        amendHref ? (
          <Button asChild variant="outline">
            <Link to={amendHref}>Start an amendment</Link>
          </Button>
        ) : undefined
      }
    />
  );
}

export function PermissionDenied({ body }: { body?: ReactNode }) {
  return (
    <Shell
      tone="info"
      icon={Lock}
      title="You do not have access"
      body={body ?? "This record belongs to someone else, or your supervision link has ended."}
      action={
        <Button asChild variant="outline">
          <Link to="/">Go to home</Link>
        </Button>
      }
    />
  );
}

export function SessionExpired({ onSignIn }: { onSignIn?: () => void }) {
  return (
    <Shell
      tone="warning"
      icon={UserRoundX}
      title="Please sign in again"
      body="Your session ended. Nothing you saved is lost."
      action={
        onSignIn ? (
          <Button onClick={onSignIn}>Sign in</Button>
        ) : (
          <Button asChild>
            <Link to="/sign-in">Sign in</Link>
          </Button>
        )
      }
    />
  );
}

export function NotFound() {
  return (
    <Shell
      tone="other"
      icon={FileQuestion}
      title="Page not found"
      body="The link may be old, or the record may have been removed."
      action={
        <Button asChild variant="outline">
          <Link to="/">Go to home</Link>
        </Button>
      }
    />
  );
}

/** Shown while the browser is offline. Work stays on screen; saving waits. */
export function OfflineBanner({ online }: { online: boolean }) {
  if (online) return null;
  return (
    <div
      role="status"
      className="bg-warning text-warning-foreground flex items-center justify-center gap-2 px-4 py-2"
    >
      <CloudOff aria-hidden="true" className="size-[17px]" />
      <Overline>Offline</Overline>
      <BodySm className="text-warning-foreground">
        Keep logging. Saving waits until you are back.
      </BodySm>
    </div>
  );
}
